import React, { memo, useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { TagSelect, Select, SelectOptionWithGroup, SelectMultiple, StatusTip, StatusTipProps, BubbleContent } from 'tea-component';
import { changeDropDownListOnSearchEmptyText } from '@gp/utils/common';
import { isEqual } from 'lodash-es';
import classNames from 'classnames';
import { SELECT_PANEL_HEIGHT, SELECT_PANEL_HEIGHT_WITHOUT_SEARCH_BOX, CONSTANT_TEXTS } from '@gp/constants/const';
import { OptType, DropDownSelectPropsType } from './index.d';
import './index.scss';

const MULTIPLE_TYPE = ['selectMultiple', 'tagSelect'];

const getDisabledStyle = (setIsRenderBubble: React.Dispatch<React.SetStateAction<boolean>>, bubbleContent: string[] | string | undefined) => {
  const bubble = document.querySelector('.gp-public-drop-down-disabled-select-item-bubble');
  if (!bubble) setIsRenderBubble(true);
  setTimeout(() => {
    const disabledArr = document.querySelectorAll('#tea-overlay-root .tea-list .is-disabled .tea-checkbox');
    for (let i = 0; i < disabledArr?.length; i++) {
      const t = disabledArr?.[i];
      t?.removeAttribute('disabled');
      const bubble: any = document.querySelector('.gp-public-drop-down-disabled-select-item-bubble');
      if (bubbleContent || bubbleContent?.[i]) {
        bubble.style.display = 'block';
        bubble.style.top = -10000 + 'px';
        bubble.style.left = -10000 + 'px';
        bubble.firstChild.innerText = Array?.isArray(bubbleContent) ? bubbleContent[i] || '-' : bubbleContent || '-';
        const offsetW = bubble?.clientWidth / 2, offsetH = bubble?.clientHeight + 12;
        t?.addEventListener('mousemove', (e) => {
          //@ts-ignore
          const x = e?.pageX - offsetW, y = e?.pageY - offsetH;
          bubble.style.top = y + 'px';
          bubble.style.left = x + 'px';
        });
        t?.addEventListener('mouseout', (e) => {
          bubble.style.top = -10000 + 'px';
          bubble.style.left = -10000 + 'px';
        });
      }
    }
  }, 200);
}

const DEFAULT_OPT: OptType = {
  type: 'select',
  staging: true,
  clearable: true,
  placeholder: 'Please select one item',
  searchPlaceholder: '',
  defaultValue: undefined,
  isValueControlled: false,
  values: undefined,
  hasDisabledItem: false,
  optionsDefaultDisabledBubbleContent: undefined,
  optionsDisabledItemsBubbleContent: [],
  options: undefined,
  listWidth: undefined,
  listHeight: undefined,
  disabled: false,
  searchable: false,
  initEmptyText: CONSTANT_TEXTS.EMPTY_TEXT,
  emptyText: CONSTANT_TEXTS.EMPTY_TEXT,
  isOuterLoading: false,
  isCustomButtonCtx: false,
  services: null,
  paramFormat: undefined,
};

const DEFAULT_PROPS: DropDownSelectPropsType = {
  className: '',
  boxClassName: '',
  customStyle: {},
  allOpt: DEFAULT_OPT,
  onSuccessSearch: null,
  onChange: null,
  getButtonCtx: null,
  loading: false,
};

function index(props: DropDownSelectPropsType) {
  const {
    className,
    boxClassName,
    customStyle,
    allOpt,
    onSuccessSearch,
    getButtonCtx,
    onChange,
    loading,
  } = props;

  const [searchLoading, setSearchLoading] = useState<StatusTipProps['status'] | null>(null);
  const [options, setOptions] = useState<any>([]);
  const [lastOptions, setLastOptions] = useState<any>([]);
  // 暂存选中数据的配置项，多选搜索时配置项可能丢失
  const [selectMultipleOpt, setSelectMultipleOpt] = useState<any>(null);
  // 用于防抖
  const executeServicesRef = useRef<any>(null);
  // 是否渲染bubble提示的DOM节点
  const [isRenderBubble, setIsRenderBubble] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
  // 上一次搜索的内容，搜索内容连续相同时，不请求接口
  const [lastSearchValue, setLastSearchValue] = useState<string | undefined>(undefined);

  const newOpt: OptType = useMemo(() => ({...DEFAULT_OPT, ...allOpt}), [allOpt]);
  const listHeight = useMemo<number>(() => {
    const { listHeight: newOptListHeight, searchable: newOptSearchable } = newOpt || {};
    if (newOptListHeight) return newOptListHeight;
    return newOptSearchable ? SELECT_PANEL_HEIGHT : SELECT_PANEL_HEIGHT_WITHOUT_SEARCH_BOX;
  }, [newOpt]);

  // 默认数据
  useEffect(() => {
    if (MULTIPLE_TYPE?.includes(allOpt?.type as string) && Array.isArray(allOpt?.defaultValue) && allOpt?.defaultValue?.length) {
      const selectItems: any = [];
      for (let i = 0; i < (allOpt?.defaultValue as any[])?.length; i ++) {
        const it = allOpt?.defaultValue?.[i];
        const selectItem = allOpt?.options?.find((t: any) => t?.value === it);
        selectItem && selectItems?.push(selectItem);
      }
      if (selectItems?.length && !selectMultipleOpt?.length) {
        setSelectMultipleOpt(selectItems);
      }
    }
    if (MULTIPLE_TYPE?.includes(allOpt?.type as string) && Array.isArray(allOpt?.values) && allOpt?.values?.length) {
      const selectItems: any = [];
      for (let i = 0; i < (allOpt?.values as any[])?.length; i ++) {
        const it = allOpt?.values?.[i];
        const selectItem = allOpt?.options?.find((t: any) => t?.value === it);
        selectItem && selectItems?.push(selectItem);
      }
      if (selectItems?.length && !selectMultipleOpt?.length) {
        setSelectMultipleOpt(selectItems);
      }
    }
  }, [allOpt?.type, allOpt?.defaultValue, allOpt?.values, allOpt?.options]);

  // 受外部loading控制时，同步loading态
  useEffect(() => {
    if (newOpt?.isOuterLoading) {
      setSearchLoading(loading ? 'loading' : null);
    }
  }, [loading]);

  useEffect(() => {
    if (Array.isArray(newOpt.options)) {
      setOptions([...newOpt.options]);
      newOpt?.options?.length && setLastOptions(newOpt.options);
    }
    newOpt.hasDisabledItem && getDisabledStyle(setIsRenderBubble, newOpt.optionsDisabledItemsBubbleContent);
  }, [newOpt.options, newOpt.optionsDisabledItemsBubbleContent]);

  // 搜索为空（配置项为空，这里配置项都是外部传进来的）时修改DOM文案
  useEffect(() => {
    const emptyText = !!searchValue ? newOpt?.emptyText : newOpt?.initEmptyText;
    if (allOpt?.type === 'tagSelect') {
      (!searchLoading && (!options?.length || (options?.length === selectMultipleOpt?.length))) && changeDropDownListOnSearchEmptyText(true, emptyText);
    } else {
      (!searchLoading && !options?.length) && changeDropDownListOnSearchEmptyText(true, emptyText);
    }
  }, [options, searchLoading]);

  const handleOpen = useCallback(() => {
    setSearchValue(undefined);
    const emptyText = newOpt?.initEmptyText;
    if (allOpt?.type === 'tagSelect') {
      // 打开时配置项为空修改DOM文案，或者当所有配置项都被选中时，修改为空的DOM文案
      if (!searchLoading && (!options?.length || (options?.length === selectMultipleOpt?.length))) setTimeout(() => changeDropDownListOnSearchEmptyText(true, emptyText), 10);

      // 当配置项没内容，有选中项时去展示选中项，没有时展示上一次搜索的项或者空态
      if (!options?.length && selectMultipleOpt) {
        setOptions(selectMultipleOpt || []);
        setLastOptions(selectMultipleOpt || []);
      }
      return;
    }

    // 打开时配置项为空修改DOM文案
    if (!searchLoading && !options?.length && !selectMultipleOpt?.length) {
      setTimeout(() => changeDropDownListOnSearchEmptyText(true, emptyText), 10);
    }

    // 有选中项时去展示选中项，没有时展示上一次搜索的项或者空态
    if (selectMultipleOpt) setOptions(selectMultipleOpt || []);

    // 为禁用item添加hover事件
    if (newOpt?.type === 'selectMultiple') {
      newOpt.hasDisabledItem && getDisabledStyle(setIsRenderBubble, newOpt.optionsDefaultDisabledBubbleContent);
    }
  }, [
    options,
    searchValue,
    newOpt.initEmptyText,
    newOpt.emptyText,
    newOpt.type,
    searchLoading,
    selectMultipleOpt,
    newOpt.optionsDefaultDisabledBubbleContent,
  ]);

  /* const handleClose = useCallback(() => {
    setOptions(selectMultipleOpt || []);
  }, [selectMultipleOpt]); */

  const handleChange = useCallback((value: string | string[]) => {
    // 点击清空按钮或者标签的删除按钮删为空
    if (!value || value?.length === 0) {
      setSelectMultipleOpt(null);
      // setOptions([]);
      onChange?.({
        value: Array.isArray(value) ? [] : '',
        options: [],
      });
      return;
    }

    if (Array.isArray(value)) {
      // 找到现有配置项能找到的选中的配置项
      const currentSelectOpt = options ? options?.filter((t: any) => value?.includes(t?.value)) : [];
      // 找到之前配置项能找到的选中的配置项
      const oldSelectOpt = selectMultipleOpt ? selectMultipleOpt?.filter((t: any) => value?.includes(t?.value)) : [];
      const allSelectOpt = [...oldSelectOpt, ...currentSelectOpt];
      // 去除重复配置项
      const realSelectOpt: any = [], alikeValue: string[] = [];
      for (let i = 0; i < allSelectOpt?.length; i++) {
        const t = allSelectOpt?.[i];
        if (!alikeValue?.includes(t?.value)) {
          alikeValue.push(t?.value);
          realSelectOpt.push(t);
        }
      }
      setSelectMultipleOpt(realSelectOpt);
      // setOptions(realSelectOpt);

      const res = {
        value,
        options: realSelectOpt,
      };
      onChange?.(res);
    } else {
      const res = {
        value,
        options: newOpt?.options?.filter((t: any) => t?.value === value),
      };
      onChange?.(res);
    }
  }, [options, selectMultipleOpt]);

  const handleSearch = useCallback(async (keyword: string | undefined) => {
    const searchValue = keyword?.trim();
    setSearchValue(searchValue);
    if (searchValue == lastSearchValue) return;
    if (allOpt?.type === 'tagSelect' && searchValue) {
      // tagSelect时，搜索不为空才储存搜索值，搜索为空的话还是保留上一次搜索的值，因为options也是一样
      setLastSearchValue(searchValue);
    } else if (allOpt?.type !== 'tagSelect') {
      setLastSearchValue(searchValue);
    }

    // 搜索为空展示已选中配置项
    if (!searchValue) {
      clearTimeout(executeServicesRef.current);
      executeServicesRef.current = null;
      changeDropDownListOnSearchEmptyText(true, newOpt?.initEmptyText);
      if (allOpt?.type === 'tagSelect') {
        setOptions(Array.isArray(lastOptions) ? [...lastOptions] : []);
      } else {
        setOptions(Array.isArray(selectMultipleOpt) ? [...selectMultipleOpt] : []);
      }
      // 为禁用item添加hover事件
      if (newOpt?.type === 'selectMultiple') {
        newOpt.hasDisabledItem && getDisabledStyle(setIsRenderBubble, newOpt.optionsDefaultDisabledBubbleContent);
      }
      setSearchLoading(null);
      return;
    }

    setSearchLoading('loading');
    if (executeServicesRef.current) clearTimeout(executeServicesRef.current);
    executeServicesRef.current = setTimeout(async () => {
      if (typeof newOpt?.services === 'function') {
        const keys = newOpt?.paramFormat?.keys, defaultParams = newOpt?.paramFormat?.params;
        let params;
        if (Array.isArray(keys)) {
          // 其他动态参数还未可知
          for (let i = 0; i < keys?.length; i++) {
            const t = keys?.[i];
            params = {...defaultParams, [t]: keyword?.trim()}
          }
        } else {
          params = defaultParams;
        }
        setOptions([]);
        const res = await newOpt?.services?.(params);
        onSuccessSearch?.(res, searchValue);
      } else {
        setOptions([]);
      }
      newOpt?.isOuterLoading || setSearchLoading(null);
    }, 300);
  }, [
    newOpt.services,
    newOpt.paramFormat,
    onSuccessSearch,
    selectMultipleOpt,
    newOpt.optionsDefaultDisabledBubbleContent,
    newOpt?.isOuterLoading,
    newOpt?.initEmptyText,
    lastSearchValue,
    lastOptions,
  ]);

  return (
    <>
      <div className={`dbg-common-dropdown ${className}`} style={customStyle}>
        {isRenderBubble && createPortal(
          (<BubbleContent
            className="gp-public-drop-down-disabled-select-item-bubble"
            dark
            // 显示的内容通过动态修改DOM文案显示
          >
            <span>-</span>
          </BubbleContent>),
          document.body
        )}
        {newOpt.type === 'select' && (
          <Select
            key={newOpt.defaultValue}
            size="full"
            appearance="button"
            matchButtonWidth
            listWidth={newOpt?.listWidth}
            // listHeight={newOpt?.listHeight}
            listHeight={listHeight}
            clearable={newOpt.clearable}
            placeholder={newOpt.placeholder}
            searchPlaceholder={newOpt.searchPlaceholder}
            options={options as SelectOptionWithGroup[]}
            button={(newOpt.isCustomButtonCtx && getButtonCtx) ? (selectedOptions: any) => getButtonCtx?.(selectedOptions) : undefined}
            defaultValue={newOpt.defaultValue}
            value={newOpt?.isValueControlled ? newOpt?.values || '' : undefined}
            onChange={handleChange}
            disabled={newOpt.disabled}
            searchable={newOpt.searchable}
            searchValue={newOpt?.searchValue}
            onSearchValueChange={newOpt?.onSearchValueChange}
            onSearch={handleSearch}
            filter={(inputValue, options) => true}
            filterRender={(inputValue, options) => options?.text}
            tips={searchLoading ? <StatusTip status={searchLoading as StatusTipProps['status']} /> : undefined}
            boxClassName={classNames({
              'globalpedia-custom-scrollbar globalpedia-modal-font14 gp-public-drop-down-modal-box': true,
              [boxClassName as string]: boxClassName,
              'globalpedia-filter-search-list-empty': !options?.length,
            })}
            onOpen={handleOpen}
          />
        )}
        {newOpt.type === 'selectMultiple' && (
          <SelectMultiple
            key={newOpt.defaultValue}
            size="full"
            appearance="button"
            button={(newOpt.isCustomButtonCtx && getButtonCtx) ? (selectedOptions: any) => getButtonCtx?.(selectedOptions) : undefined}
            matchButtonWidth
            listWidth={newOpt?.listWidth}
            // listHeight={newOpt?.listHeight}
            listHeight={listHeight}
            staging={newOpt?.staging && (searchLoading === 'loading' ? false : !!options?.length)}
            // clearable={newOpt.clearable}
            clearable={false}
            placeholder={newOpt.placeholder}
            searchPlaceholder={newOpt.searchPlaceholder}
            options={options as SelectOptionWithGroup[]}
            defaultValue={newOpt.defaultValue}
            value={newOpt?.isValueControlled ? (newOpt?.values?.length ? newOpt?.values : []) : undefined}
            onChange={handleChange}
            disabled={newOpt.disabled}
            searchable={newOpt.searchable}
            searchValue={newOpt?.searchValue}
            onSearchValueChange={newOpt?.onSearchValueChange}
            onSearch={handleSearch}
            filter={(inputValue, options) => true}
            filterRender={(inputValue, options) => options?.text}
            tips={searchLoading ? <StatusTip status={searchLoading as StatusTipProps['status']} /> : undefined}
            boxClassName={classNames({
              'globalpedia-custom-scrollbar globalpedia-modal-font14 gp-public-drop-down-modal-box': true,
              [boxClassName as string]: boxClassName,
              'globalpedia-filter-search-list-empty': !options?.length,
            })}
            onOpen={handleOpen}
            // onClose={handleClose}
          />
        )}
        {allOpt?.type === 'tagSelect' && (
          <TagSelect
            key={newOpt.defaultValue}
            optionsOnly
            options={options}
            defaultValue={newOpt.defaultValue}
            value={newOpt?.isValueControlled ? (newOpt?.values?.length ? newOpt?.values : []) : undefined}
            placeholder={newOpt.placeholder}
            disabled={newOpt.disabled}
            onChange={handleChange}
            tips={searchLoading ? <StatusTip status={searchLoading as StatusTipProps['status']} /> : undefined}
            boxClassName={classNames({
              'globalpedia-custom-scrollbar globalpedia-modal-font14 gp-public-drop-down-modal-box': true,
              [boxClassName as string]: boxClassName,
              'globalpedia-filter-search-list-empty': !options?.length,
            })}
            onOpenChange={handleOpen}
            onSearch={handleSearch}
            filter={(inputValue, options) => true}
            // filterRender={(inputValue, options) => options?.text}
            // onClose={handleClose}
          />
        )}
      </div>
    </>
  )
}

index.defaultProps = DEFAULT_PROPS;

function areEqual(prevProps: any, nextProps: any) {
  return isEqual(prevProps, nextProps); // 减少不必要的渲染
}

export default memo(index, areEqual);
